import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, ShieldCheck, Banknote, AlertTriangle,
  Send, Users, Database, Stars, HeartPulse, Hospital, BarChart3,
  LogOut, Bell, Search, Stethoscope, Mic, Brain, Scale, Pill,
  GraduationCap, UserCog, Wallet, Activity, Siren, Video, Coins,
  Star, FileSignature, Layers, Sparkles, Shield, KeyRound, BookOpen,
  ChevronDown, ScrollText, TrendingUp, Menu, X as XIcon, Heart,
  Clock, ClipboardList, Link2, Languages,
} from "lucide-react";
import { auth } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import ARIA from "@/components/ARIA";
import useIdleLogout from "@/hooks/useIdleLogout";
import { DisclaimerFooter } from "@/components/Disclaimer";
import DisclaimerViewer from "@/components/DisclaimerViewer";
import { canAccess, ROLE_LABELS, requiresCoSign } from "@/lib/rbac";

const groups = [
  {
    label: "CamelionAI Bridge",
    items: [
      { to: "/bridge", label: "Bridge · 8 Connections", icon: Link2, tid: "nav-bridge" },
    ],
  },
  {
    label: "Clinical Intelligence",
    items: [
      { to: "/charting", label: "AI Charting", icon: FileText, tid: "nav-charting" },
      { to: "/prior-auth", label: "Prior Authorization", icon: ShieldCheck, tid: "nav-prior-auth" },
      { to: "/liability", label: "Liability Shield", icon: AlertTriangle, tid: "nav-liability" },
      { to: "/referrals", label: "Referrals", icon: Send, tid: "nav-referrals" },
      { to: "/predictive", label: "Predictive AI", icon: Brain, tid: "nav-predictive" },
      { to: "/risk-stratification", label: "Risk Stratification", icon: Layers, tid: "nav-risk" },
      { to: "/emergency-protocols", label: "Emergency Protocols", icon: Siren, tid: "nav-emergency" },
    ],
  },
  {
    label: "Revenue & Legal",
    items: [
      { to: "/revenue-cycle", label: "Revenue Cycle", icon: Banknote, tid: "nav-revenue" },
      { to: "/legal-intelligence", label: "Legal Intelligence", icon: Scale, tid: "nav-legal" },
      { to: "/contract-intelligence", label: "Contract AI", icon: FileSignature, tid: "nav-contract" },
    ],
  },
  {
    label: "Care Team",
    items: [
      { to: "/pa-np-hub", label: "PA & NP Hub", icon: UserCog, tid: "nav-panp" },
      { to: "/peer-consult", label: "Peer Consult", icon: Users, tid: "nav-peer" },
    ],
  },
  {
    label: "Voice & AI",
    items: [
      { to: "/dictation", label: "ARIA Dictation", icon: Mic, tid: "nav-dictation" },
      { to: "/documentation-pro", label: "Documentation Pro", icon: Sparkles, tid: "nav-doc-pro" },
      { to: "/encounter-prep", label: "Encounter Prep", icon: ClipboardList, tid: "nav-encounter-prep" },
      { to: "/voice-engine", label: "Voice Engine", icon: Mic, tid: "nav-voice" },
    ],
  },
  {
    label: "Performance",
    items: [
      { to: "/eod", label: "End of Day Mode", icon: ScrollText, tid: "nav-eod" },
      { to: "/wellbeing", label: "Wellbeing", icon: Heart, tid: "nav-wellbeing" },
      { to: "/time-saved", label: "Time Saved", icon: Clock, tid: "nav-time-saved" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/staffing", label: "Staffing", icon: Users, tid: "nav-staffing" },
      { to: "/ehr-bridge", label: "EHR Bridge", icon: Database, tid: "nav-ehr" },
      { to: "/managed-care", label: "Managed Care", icon: Stars, tid: "nav-managed-care" },
      { to: "/hospital-ops", label: "Hospital Ops", icon: Hospital, tid: "nav-hospital" },
      { to: "/care-coordination", label: "Care Coordination", icon: HeartPulse, tid: "nav-care" },
      { to: "/pharma-manager", label: "Pharma", icon: Pill, tid: "nav-pharma" },
      { to: "/cme-tracker", label: "CME & Licensing", icon: GraduationCap, tid: "nav-cme" },
      { to: "/telehealth", label: "Telehealth", icon: Video, tid: "nav-telehealth" },
    ],
  },
  {
    label: "Patient Engagement",
    items: [
      { to: "/patient-engagement", label: "Patient Engagement", icon: Stethoscope, tid: "nav-engagement" },
      { to: "/reputation-manager", label: "Reputation", icon: Star, tid: "nav-rep" },
    ],
  },
  {
    label: "Specialties",
    items: [
      { to: "/cardiology", label: "Cardiology", icon: Heart, tid: "nav-cardiology" },
    ],
  },
  {
    label: "Data & Finance",
    items: [
      { to: "/outcomes", label: "Outcome Tracking", icon: TrendingUp, tid: "nav-outcomes" },
      { to: "/financial-wellness", label: "Financial Wellness", icon: Wallet, tid: "nav-fin" },
      { to: "/data-intelligence", label: "Data Ownership", icon: Coins, tid: "nav-data" },
      { to: "/analytics", label: "Analytics", icon: BarChart3, tid: "nav-analytics" },
    ],
  },
];

export default function Layout({ children }) {
  useIdleLogout();
  const user = auth.user();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef(null);
  const onLogout = () => { auth.logout(); navigate("/login"); };

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close mobile nav drawer whenever route changes
  // eslint-disable-next-line
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const showPwBanner = user?.password_status === "soon" || user?.password_status === "overdue";

  return (
    <div className="flex min-h-screen bg-brand-bg">
      {/* Mobile drawer overlay */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-overlay" />
      )}

      {/* Sidebar — drawer on mobile, static on desktop */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-[280px] md:w-[260px] shrink-0 bg-white border-r border-brand flex flex-col transform transition-transform duration-200 ease-out
          ${mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar">
        <div className="px-5 py-5 border-b border-brand flex items-center justify-between">
          <NavLink to="/dashboard" className="flex items-center gap-2.5" data-testid="logo-link">
            <div className="w-9 h-9 rounded-lg bg-brand-primary flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-brand-primary">ZidaMD</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">by CamelionAI</div>
            </div>
          </NavLink>
          <button onClick={() => setMobileNavOpen(false)}
            className="md:hidden p-2 -mr-1 rounded-md hover:bg-brand-bg" data-testid="mobile-nav-close" aria-label="Close menu">
            <XIcon className="w-5 h-5 text-brand-muted" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <NavLink to="/dashboard"
            className={({isActive}) => `nav-link mb-1 ${isActive ? "active" : ""}`}
            data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" strokeWidth={1.6} />
            Command Center
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/security-admin"
              className={({isActive}) => `nav-link mb-1 ${isActive ? "active" : ""}`}
              data-testid="nav-security">
              <Shield className="w-4 h-4" strokeWidth={1.6} />
              Security Admin
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin/leads"
              className={({isActive}) => `nav-link mb-1 ${isActive ? "active" : ""}`}
              data-testid="nav-admin-leads">
              <Users className="w-4 h-4" strokeWidth={1.6} />
              Public Leads
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin/learning-queue"
              className={({isActive}) => `nav-link mb-1 ${isActive ? "active" : ""}`}
              data-testid="nav-learning-queue">
              <BookOpen className="w-4 h-4" strokeWidth={1.6} />
              Learning Queue
            </NavLink>
          )}
          <NavLink to="/patient-outputs"
            className={({isActive}) => `nav-link mb-1 ${isActive ? "active" : ""}`}
            data-testid="nav-patient-outputs">
            <Languages className="w-4 h-4" strokeWidth={1.6} />
            Patient Outputs
          </NavLink>
          {groups.map((g) => {
            const visible = g.items.filter(it => canAccess(user?.role, it.to));
            if (visible.length === 0) return null;
            return (
              <div key={g.label}>
                <div className="nav-group-label">{g.label}</div>
                {visible.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink key={it.to} to={it.to}
                      className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}
                      data-testid={it.tid}>
                      <Icon className="w-4 h-4" strokeWidth={1.6} />
                      {it.label}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-brand p-4 text-[11px] text-brand-muted leading-relaxed">
          <div className="font-medium text-brand-text mb-0.5">CamelionAI LLC</div>
          <div>Boca Raton, FL</div>
          <div className="mt-1.5">Patent Pending USPTO #64/078,696 | #64/078,734 | #64/062,147</div>
          <div>Support: admin@zidamd.com</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-brand px-4 md:px-6 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 text-sm text-brand-muted min-w-0">
            <button onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-md hover:bg-brand-bg" data-testid="mobile-nav-open" aria-label="Open menu">
              <Menu className="w-5 h-5 text-brand-text" />
            </button>
            <Search className="w-4 h-4 hidden md:block" />
            <span className="hidden sm:inline truncate">
              Built to adapt. Designed to perform.
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <button className="relative p-2 rounded-md hover:bg-brand-bg" data-testid="notification-btn">
              <Bell className="w-4 h-4 text-brand-muted" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-brand-teal"></span>
            </button>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                className="flex items-center gap-2.5 px-2 py-1 rounded-md hover:bg-brand-bg transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-semibold">
                  {user?.name?.split(" ").map(s=>s[0]).slice(0,2).join("")}
                </div>
                <div className="text-xs text-left hidden sm:block">
                  <div className="font-medium text-brand-text" data-testid="header-user-name">{user?.name}</div>
                  <div className="text-brand-muted">
                    {ROLE_LABELS[user?.role] || user?.role}
                    {requiresCoSign(user?.role) && (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-700 font-semibold">• Co-sign</span>
                    )}
                  </div>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-brand-muted transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-brand rounded-lg shadow-xl py-1.5 z-40" data-testid="user-menu">
                  <div className="px-3 py-2 border-b border-brand">
                    <div className="text-xs text-brand-muted">Signed in as</div>
                    <div className="text-sm font-medium truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); setViewerOpen(true); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-brand-bg text-left"
                    data-testid="menu-reread-disclaimer"
                  >
                    <ScrollText className="w-4 h-4 text-brand-primary" />
                    Re-read disclaimer
                  </button>
                  {user?.role === "admin" && (
                    <button
                      onClick={() => { setMenuOpen(false); navigate("/security-admin"); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-brand-bg text-left"
                      data-testid="menu-security"
                    >
                      <Shield className="w-4 h-4 text-brand-primary" />
                      Security Admin
                    </button>
                  )}
                  <div className="border-t border-brand mt-1 pt-1">
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-brand-bg text-left text-red-600"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 py-5 md:py-7 fade-up" key={location.pathname}>
          {user?.twofa_skipped_by_user && !user?.twofa_enabled && (
            <div className="mb-5 p-3 rounded-md text-sm flex items-center justify-between bg-amber-50 border border-amber-200 text-amber-900" data-testid="twofa-skipped-banner">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <div>
                  <b>Your account security may be compromised.</b> Two-factor authentication is strongly recommended.
                  You can enable it anytime under{" "}
                  <NavLink to="/security-admin" className="underline font-semibold">Account Settings</NavLink>.
                </div>
              </div>
            </div>
          )}
          {showPwBanner && (
            <div className={`mb-5 p-3 rounded-md text-sm flex items-center justify-between ${user?.password_status === "overdue" ? "bg-red-50 border border-red-200 text-red-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`} data-testid="pw-banner">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                {user?.password_status === "overdue"
                  ? `Password is ${user?.password_age_days} days old — rotation overdue.`
                  : `Password is ${user?.password_age_days} days old — rotation recommended.`}
              </div>
              <button onClick={() => navigate("/security-admin")} className="text-xs underline">Change now</button>
            </div>
          )}
          {children}
        </main>
        <DisclaimerFooter />
      </div>

      <VoiceButton />
      <ARIA />
      <MobileBottomNav user={user} />
      {viewerOpen && <DisclaimerViewer onClose={() => setViewerOpen(false)} />}
    </div>
  );
}

// ---------- Mobile bottom navigation (5 most-used modules) ----------
function MobileBottomNav({ user }) {
  const items = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, tid: "bn-home" },
    { to: "/charting", label: "Chart", icon: FileText, tid: "bn-chart" },
    { to: "/prior-auth", label: "PA", icon: ShieldCheck, tid: "bn-pa" },
    { to: "/outcomes", label: "Outcomes", icon: TrendingUp, tid: "bn-outcomes" },
    { to: "/referrals", label: "Refer", icon: Send, tid: "bn-refer" },
  ].filter(it => canAccess(user?.role, it.to));
  if (items.length === 0) return null;
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-brand shadow-[0_-4px_12px_rgba(74,14,143,0.08)]" data-testid="mobile-bottom-nav" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <ul className="flex justify-around items-stretch">
        {items.map(it => {
          const Icon = it.icon;
          return (
            <li key={it.to} className="flex-1">
              <NavLink to={it.to}
                className={({isActive}) => `flex flex-col items-center justify-center py-2 px-1 text-[10px] font-medium gap-0.5 transition-colors ${isActive ? "text-brand-primary" : "text-brand-muted hover:text-brand-text"}`}
                data-testid={it.tid}
                style={{ minHeight: 56 }}>
                <Icon className="w-5 h-5" strokeWidth={1.8} />
                <span className="leading-none">{it.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
